import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Chip,
  Container,
  Grid,
  CircularProgress,
  Backdrop,
  Stack,
  Toolbar,
  AppBar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import ShortlistDetailsForm from "./ShortlistDetailsForm";

/**
 * JDWithL0 component is a React functional component that displays a job description 
 * along with a call to action to apply for a job. It fetches job details from the 
 * server and displays them to the user. It also allows the user to schedule an 
 * interview for the job candidate.
 *
 * @return {JSX.Element} The JSX element representing the JDWithL0 component.
 */
export default function JDWithL0() {
  const { token } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialog, setDialog] = useState({
      open: false,
      title: "",
      message: "",
    });
  
  const [availability, setAvailability] = useState({
        date: "",
        time: "",
    });
  const [jd, setJd] = useState(null);
  const [skipping, setSkipping] = useState(null);
  const [resumeId, setResumeId] = useState(null);
  const [jdId, setJdId] = useState(null);

  const navigate = useNavigate();

  /**
   * Resolves the token and sets the resume and job details.
   */
  useEffect(() => {
    if (!token) return;

    const resolveToken = async () => {
      try {
        const res = await api.get(`/resolve-token/${token}/`);
        setResumeId(res.data.resume_id);
        setJdId(res.data.jd_id);
      } catch {
        setDialog({
        open: true,
        title: "Invalid Job Link",
        message: "This job link is no longer valid or has expired.",
      })
      }
    };

    resolveToken();
  }, [token]);

  /**
   * Fetches job details based on the provided job detail ID.
   *
   * @return {Promise<void>} A Promise that resolves when the job details are fetched.
   * @throws {Error} If the job details can't be fetched.
   */
  useEffect(() => {
    if (!jdId) return;

    /**
     * Loads the job details from the server based on the job detail ID.
     *
     * @return {Promise<void>} A Promise that resolves when the job details are loaded.
     * @throws {Error} If the job details can't be loaded.
     */
    const loadJob = async () => {
      try {
        const res = await api.get(`/career/job-detail/${jdId}/`);
        setJd(res.data);
      } catch {
        setDialog({
          open: true,
          title: "Invalid Job Link",
          message: "This job link is no longer valid or has expired.",
        })
      }
    };

    loadJob();
  }, [jdId]);

  /**
   * Fetches and sets the skipping data for the job candidate based on resume ID and job detail ID.
   *
   * @return {Promise<void>} A Promise that resolves when the skipping data is fetched and set.
   */
  useEffect(() => {
    if (!resumeId || !jdId) return;

    const value = `${resumeId}_${jdId}`;

    /**
     * Retrieves the skipping data for the job candidate from the server.
     *
     * @return {Promise<void>} A Promise that resolves when the skipping data is retrieved.
     */
    const fetchSkipping = async () => {
      try {
        const res = await api.get(`/shortlist-details/${value}/`);
        setSkipping(res.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchSkipping();
  }, [resumeId, jdId]);

  /**
   * Handles the schedule click event by opening the dialog.
   *
   * @return {void}
   */
  const handleScheduleClick = () => {
      setOpenDialog(true);
    };
  
  /**
   * Closes the dialog.
   *
   * @return {void}
   */
    const handleClose = () => {
      setOpenDialog(false);
    };
  
  /**
   * Submits the candidate's availability to the server.
   *
   * @return {Promise<void>} A Promise that resolves when the availability is submitted.
   */
    const handleSubmitAvailability = () => {
      api.post("/candidate-availability/", {
        token,
        date: availability.date,
        time: availability.time,
      })
      .then(() => {
        setDialog({
          open: true,
          title: "Success",
          message: "Availability submitted successfully!",
        })
        setOpenDialog(false);
      })
      .catch(err => {
        console.error(err);
        setDialog({
          open: true,
          title: "Error",
          message: "Something went wrong",
        })
      });
    };
    
  /**
   * Handles the start now click event by opening the interview welcome page.
   *
   * @return {void}
   */
    const handleStartNow = () => {
        window.open(`${api.interview_link}/interview_welcome?jd_id=${jdId}&resume_id=${resumeId}`);
    };

  /**
   * Formats a value with a fallback text.
   *
   * @param {string} value - The value to format.
   * @param {string} fallback - The fallback text to use if the value is not specified.
   * @return {Object} An object with the formatted text and a boolean indicating if it should be dimmed.
   */
    const formatWithFallback = (value, fallback) => {
      if (!value) return { text: fallback, dim: true };

      const v = value.toLowerCase();
      if (v.includes("not specified") || v === "n/a") {
        return { text: fallback, dim: true };
      }

      return { text: value, dim: false };
  };

  const location = formatWithFallback(jd?.job_location, "Location");
  const exp = formatWithFallback(jd?.years_of_experience, "Exp");
  const type = formatWithFallback(jd?.job_type, "Job type");
  const salary = formatWithFallback(jd?.salary_range, "Salary range");

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.9)",
          borderBottom: "1px solid #eee",
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              background: "linear-gradient(90deg,#6366f1,#ec4899)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            ITConnectUS
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 8, top: "12px" }}>
            <Box sx={{ flex: 7 }}>
            <Card sx={{ p: 6 }}>
                <Typography variant="h4" fontWeight={800} gutterBottom>
                {jd?.job_title || "Loading..."}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" mb={3}>
                <Chip label={`📍 ${location.text}`} sx={{ color: location.dim ? "#9e9e9e" : "#000" }} />
                <Chip label={`🧠 ${exp.text}`} sx={{ color: exp.dim ? "#9e9e9e" : "#000" }} />
                <Chip label={`💼 ${type.text}`} sx={{ color: type.dim ? "#9e9e9e" : "#000" }} />
                <Chip label={`💰 ${salary.text}`} sx={{ color: salary.dim ? "#9e9e9e" : "#000" }} />
                </Stack>

                <Typography variant="h5" fontWeight={600} gutterBottom>
                    If you are interested in this position/AI Pre-screening, 
                    please fill in the required details by clicking{" "}
                    <Button
                        variant="contained"
                        sx={{ textTransform: "none" }}
                        onClick={() => navigate(`/shortlist-details/${token}`)}
                    >
                        Here
                    </Button>
                </Typography>
        
                {jd?.job_summary && (
                <Box mt={4}>
                    <Typography variant="h6" fontWeight={700}>
                    About the Role
                    </Typography>
                    <ul>
                    {jd.job_summary.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography>{r}</Typography>
                        </li>
                    ))}
                    </ul>
                </Box>
                )}

                {jd?.responsibilities && (
                <>
                    <Typography variant="h6" fontWeight={700} mt={3}>
                    Responsibilities
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                    {jd.responsibilities.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                        </li>
                    ))}
                    </Box>
                </>
                )}

                {jd?.domain_requirements && (
                <>
                    <Typography variant="h6" fontWeight={700} mt={3}>
                    Doamin Requirements
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                    {jd.domain_requirements.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                        </li>
                    ))}
                    </Box>
                </>
                )}

                {jd?.required_qualifications && (
                <>
                    <Typography variant="h6" fontWeight={700} mt={3}>
                    Required Qualifications
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                    {jd.required_qualifications.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                        </li>
                    ))}
                    </Box>
                </>
                )}

                {jd?.preferred_qualifications && (
                <>
                    <Typography variant="h6" fontWeight={700} mt={3}>
                    Preferred Qualifications
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                    {jd.preferred_qualifications.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                        </li>
                    ))}
                    </Box>
                </>
                )}

                {jd?.education_qualifications && (
                <>
                    <Typography variant="h6" fontWeight={700} mt={3}>
                    Education Qualifications
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                    {jd.education_qualifications.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                        </li>
                    ))}
                    </Box>
                </>
                )}

                {jd?.certification_requirements && (
                <>
                    <Typography variant="h6" fontWeight={700} mt={3}>
                    Certification Requirements
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                    {jd.certification_requirements.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                        </li>
                    ))}
                    </Box>
                </>
                )}

                {jd?.security_clearance_requirements && (
                <>
                    <Typography variant="h6" fontWeight={700} mt={3}>
                    Security Clearance
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                    {jd.certification_requirements.split("\n").map((r, i) => (
                        <li key={i}>
                        <Typography variant="body1">{r}</Typography>
                        </li>
                    ))}
                    </Box>
                </>
                )}
            </Card>
            </Box>
            {skipping && !skipping.already_submitted && (
            <Box display="flex" justifyContent="center" mt={6}>
                <Dialog open={openDialog} onClose={handleClose} fullWidth>
                <DialogTitle>Schedule Your Interview</DialogTitle>
        
                <DialogContent>
                    <TextField
                    label="Preferred Date"
                    type="date"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    value={availability.date}
                    onChange={(e) =>
                        setAvailability({ ...availability, date: e.target.value })
                    }
                    />
        
                    <TextField
                    label="Preferred Time"
                    type="time"
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    value={availability.time}
                    onChange={(e) =>
                        setAvailability({ ...availability, time: e.target.value })
                    }
                    />
                </DialogContent>
        
                <DialogActions>
                    <Button variant="contained" sx={{ textTransform: "none" }} onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" sx={{ textTransform: "none" }} onClick={handleSubmitAvailability}>
                    Submit
                    </Button>
                </DialogActions>
                </Dialog>
            </Box>
            )}
        </Container>
        <Dialog open={openDialog} onClose={handleClose} fullWidth>
            <DialogTitle>Schedule Your Interview</DialogTitle>
    
            <DialogContent>
                <TextField
                label="Preferred Date"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={availability.date}
                onChange={(e) =>
                    setAvailability({ ...availability, date: e.target.value })
                }
                />
    
                <TextField
                label="Preferred Time"
                type="time"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={availability.time}
                onChange={(e) =>
                    setAvailability({ ...availability, time: e.target.value })
                }
                />
            </DialogContent>
    
            <DialogActions>
                <Button variant="contained" sx={{ textTransform: "none" }} onClick={handleClose}>Cancel</Button>
                <Button variant="contained" sx={{ textTransform: "none" }} onClick={handleSubmitAvailability}>
                Submit
                </Button>
            </DialogActions>
            </Dialog>
            <Dialog
              open={dialog.open}
              onClose={() => setDialog({ ...dialog, open: false })}
            >
              <DialogTitle sx={{ fontWeight: 700 }}>
                {dialog.title}
              </DialogTitle>
              <DialogContent>
                <Typography fontSize="14px">
                  {dialog.message}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="contained"
                  onClick={() => setDialog({ ...dialog, open: false })}
                >
                  OK
                </Button>
              </DialogActions>
            </Dialog>

      <Box textAlign="center" py={6} sx={{ opacity: 0.6 }}>
        <Typography variant="body2">
          © {new Date().getFullYear()} {api.app_name}. Built for the future of hiring.
        </Typography>
      </Box>
    </>
  );
}